/**
 * CalendarSkeleton Component
 *
 * Skeleton loading state for the calendar screen.
 * Shows placeholder for calendar header, month view, and agenda items.
 */

import React from 'react';
import { StyleSheet, View } from 'react-native';

import { Colors } from '@/constants/theme';
import { Spacing, BorderRadius } from '@/constants/spacing';
import { useColorScheme } from '@/hooks/use-color-scheme';

/**
 * Props for CalendarSkeleton component
 */
export interface CalendarSkeletonProps {
  /** Number of skeleton agenda items to show */
  itemCount?: number;
}

/**
 * CalendarSkeleton - Loading placeholder for calendar view
 *
 * Displays skeleton UI matching the final calendar layout including
 * calendar header, month view grid, and agenda list items.
 */
export const CalendarSkeleton: React.FC<CalendarSkeletonProps> = ({
  itemCount = 5,
}) => {
  const colorScheme = useColorScheme();
  const colors = Colors[colorScheme ?? 'light'];

  const skeletonStyle = {
    backgroundColor: colorScheme === 'dark' ? colors.cardBackground : colors.backgroundLight,
  };

  return (
    <View style={styles.container}>
      {/* Calendar Header Skeleton */}
      <View style={styles.calendarHeader}>
        <View style={[styles.calendarMonthLabel, skeletonStyle]} />
        <View style={styles.calendarGrid}>
          {/* Week day labels */}
          <View style={styles.weekRow}>
            {Array.from({ length: 7 }).map((_, index) => (
              <View key={`day-${index}`} style={[styles.dayLabel, skeletonStyle]} />
            ))}
          </View>
          {/* Calendar dates grid (5 weeks) */}
          {Array.from({ length: 5 }).map((_, weekIndex) => (
            <View key={`week-${weekIndex}`} style={styles.weekRow}>
              {Array.from({ length: 7 }).map((_, dayIndex) => (
                <View
                  key={`date-${weekIndex}-${dayIndex}`}
                  style={[styles.dateCell, skeletonStyle]}
                />
              ))}
            </View>
          ))}
        </View>
      </View>

      {/* Agenda List Skeleton */}
      <View style={styles.agendaList}>
        {Array.from({ length: itemCount }).map((_, index) => (
          <View key={`item-${index}`} style={styles.agendaItem}>
            {/* Date header */}
            <View style={[styles.dateHeader, skeletonStyle]} />
            {/* Event card */}
            <View style={[styles.eventCard, { backgroundColor: colors.cardBackground }]}>
              <View style={[styles.eventTime, skeletonStyle]} />
              <View style={[styles.eventTitle, skeletonStyle]} />
              <View style={[styles.eventDetails, skeletonStyle]} />
            </View>
          </View>
        ))}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: Spacing.lg,
  },
  calendarHeader: {
    marginBottom: Spacing.xl,
  },
  calendarMonthLabel: {
    height: 24,
    width: 120,
    borderRadius: BorderRadius.xs,
    marginBottom: Spacing.lg,
  },
  calendarGrid: {
    gap: Spacing.sm,
  },
  weekRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: Spacing.sm,
  },
  dayLabel: {
    width: 32,
    height: 16,
    borderRadius: BorderRadius.xs,
  },
  dateCell: {
    width: 32,
    height: 32,
    borderRadius: 16, // circular
  },
  agendaList: {
    gap: Spacing.lg,
  },
  agendaItem: {
    gap: Spacing.sm,
  },
  dateHeader: {
    height: 20,
    width: 100,
    borderRadius: BorderRadius.xs,
  },
  eventCard: {
    padding: Spacing.lg,
    borderRadius: BorderRadius.md,
    gap: Spacing.sm,
  },
  eventTime: {
    height: 14,
    width: 80,
    borderRadius: BorderRadius.xs,
  },
  eventTitle: {
    height: 18,
    width: '70%',
    borderRadius: BorderRadius.xs,
  },
  eventDetails: {
    height: 14,
    width: '50%',
    borderRadius: BorderRadius.xs,
  },
});
